/// <amd-module name="Repos/Views/PullRequest/RestClient/Git" />
/**
 * ---------------------------------------------------------
 * Generated file, DO NOT EDIT
 * ---------------------------------------------------------
 *
 * See following wiki page for instructions on how to regenerate:
 *   https://aka.ms/azure-devops-client-generation
 *
 * Configuration file:
 *   tfs\web\extensions\code\vss-code-web\repos\views\pr-details\genclient.json
 */

import { IVssPageContext, IVssRestClient, IVssRestClientOptions, RestClients } from "VSS/Platform/Context";
import { RestClientBase } from "VSS/Platform/RestClientBase";

import * as Git from "Repos/Common/Contracts/Git";
import * as TFS_Platform from "Tfs/Platform/Contracts/Core";

export interface IGitRestClient extends IVssRestClient {
    /**
     * Add a like on a comment.
     * 
     * @param repositoryId - The repository ID of the pull request's target branch.
     * @param pullRequestId - ID of the pull request.
     * @param threadId - The ID of the thread that contains the comment.
     * @param commentId - The ID of the comment.
     * @param project - Project ID or project name
     */
    createLike(repositoryId: string, pullRequestId: number, threadId: number, commentId: number, project?: string): Promise<void>;
    /**
     * Delete a like on a comment.
     * 
     * @param repositoryId - The repository ID of the pull request's target branch.
     * @param pullRequestId - ID of the pull request.
     * @param threadId - The ID of the thread that contains the comment.
     * @param commentId - The ID of the comment.
     * @param project - Project ID or project name
     */
    deleteLike(repositoryId: string, pullRequestId: number, threadId: number, commentId: number, project?: string): Promise<void>;
    /**
     * Retrieve all conflicts for a pull request
     * 
     * @param repositoryId - The repository of the Pull Request.
     * @param pullRequestId - The pull request ID.
     * @param project - Project ID or project name
     * @param skip - Conflicts to skip.
     * @param top - Conflicts to return after skip.
     * @param includeObsolete - Includes obsolete conflicts.
     * @param excludeResolved - Excludes conflicts already resolved.
     * @param onlyResolved - Returns only the conflicts that are resolved.
     */
    getPullRequestConflicts(repositoryId: string, pullRequestId: number, project?: string, skip?: number, top?: number, includeObsolete?: boolean, excludeResolved?: boolean, onlyResolved?: boolean): Promise<Git.GitConflict[]>;
    /**
     * Create a tag (if that does not exists yet) and add that as a label (tag) for a specified pull request. The only required field is the name of the new label (tag).
     * 
     * @param label - Label to assign to the pull request.
     * @param repositoryId - The repository ID of the pull request’s target branch.
     * @param pullRequestId - ID of the pull request.
     * @param project - Project ID or project name
     * @param projectId - Project ID or project name.
     */
    createPullRequestLabel(label: TFS_Platform.WebApiCreateTagRequestData, repositoryId: string, pullRequestId: number, project?: string, projectId?: string): Promise<TFS_Platform.WebApiTagDefinition>;
    /**
     * Removes a label (tag) from the set of those assigned to the pull request. The tag itself will not be deleted.
     * 
     * @param repositoryId - The repository ID of the pull request’s target branch.
     * @param pullRequestId - ID of the pull request.
     * @param labelIdOrName - The name or ID of the label requested.
     * @param project - Project ID or project name
     * @param projectId - Project ID or project name.
     */
    deletePullRequestLabels(repositoryId: string, pullRequestId: number, labelIdOrName: string, project?: string, projectId?: string): Promise<void>;
    /**
     * Add a reviewer to a pull request or cast a vote.
     * 
     * @param reviewer - Reviewer's vote.\<br /\>If the reviewer's ID is included here, it must match the reviewerID parameter.\<br /\>Reviewers can set their own vote with this method.  When adding other reviewers, vote must be set to zero.
     * @param repositoryId - The repository ID of the pull request's target branch.
     * @param pullRequestId - ID of the pull request.
     * @param reviewerId - ID of the reviewer.
     * @param project - Project ID or project name
     */
    createPullRequestReviewer(reviewer: Git.IdentityRefWithVote, repositoryId: string, pullRequestId: number, reviewerId: string, project?: string): Promise<Git.IdentityRefWithVote>;
    /**
     * Add an unmaterialized identity to the reviewers of a pull request.
     * 
     * @param reviewer - Reviewer to add to the pull request.
     * @param repositoryId - The repository ID of the pull request's target branch.
     * @param pullRequestId - ID of the pull request.
     * @param project - Project ID or project name
     */
    createUnmaterializedPullRequestReviewer(reviewer: Git.IdentityRefWithVote, repositoryId: string, pullRequestId: number, project?: string): Promise<Git.IdentityRefWithVote>;
    /**
     * Remove a reviewer from a pull request.
     * 
     * @param repositoryId - The repository ID of the pull request's target branch.
     * @param pullRequestId - ID of the pull request.
     * @param reviewerId - ID of the reviewer to remove.
     * @param project - Project ID or project name
     */
    deletePullRequestReviewer(repositoryId: string, pullRequestId: number, reviewerId: string, project?: string): Promise<void>;
    /**
     * Edit a reviewer entry. These fields are patchable: isFlagged, hasDeclined
     * 
     * @param reviewer - Reviewer data.\<br /\>If the reviewer's ID is included here, it must match the reviewerID parameter.
     * @param repositoryId - The repository ID of the pull request's target branch.
     * @param pullRequestId - ID of the pull request.
     * @param reviewerId - ID of the reviewer.
     * @param project - Project ID or project name
     */
    updatePullRequestReviewer(reviewer: Git.IdentityRefWithVote, repositoryId: string, pullRequestId: number, reviewerId: string, project?: string): Promise<Git.IdentityRefWithVote>;
    /**
     * Update a pull request
     * 
     * @param gitPullRequestToUpdate - The pull request content that should be updated.
     * @param repositoryId - The repository ID of the pull request's target branch.
     * @param pullRequestId - ID of the pull request to update.
     * @param project - Project ID or project name
     */
    updatePullRequest(gitPullRequestToUpdate: Git.GitPullRequest, repositoryId: string, pullRequestId: number, project?: string): Promise<Git.GitPullRequest>;
    /**
     * Create a comment on a specific thread in a pull request (up to 500 comments can be created per thread).
     * 
     * @param comment - The comment to create. Comments can be up to 150,000 characters.
     * @param repositoryId - The repository ID of the pull request's target branch.
     * @param pullRequestId - ID of the pull request.
     * @param threadId - ID of the thread that the desired comment is in.
     * @param project - Project ID or project name
     */
    createComment(comment: Git.Comment, repositoryId: string, pullRequestId: number, threadId: number, project?: string): Promise<Git.Comment>;
    /**
     * Delete a comment associated with a specific thread in a pull request.
     * 
     * @param repositoryId - The repository ID of the pull request's target branch.
     * @param pullRequestId - ID of the pull request.
     * @param threadId - ID of the thread that the desired comment is in.
     * @param commentId - ID of the comment.
     * @param project - Project ID or project name
     */
    deleteComment(repositoryId: string, pullRequestId: number, threadId: number, commentId: number, project?: string): Promise<void>;
    /**
     * Update a comment associated with a specific thread in a pull request.
     * 
     * @param comment - The comment content that should be updated. Comments can be up to 150,000 characters.
     * @param repositoryId - The repository ID of the pull request's target branch.
     * @param pullRequestId - ID of the pull request.
     * @param threadId - ID of the thread that the desired comment is in.
     * @param commentId - ID of the comment to update.
     * @param project - Project ID or project name
     */
    updateComment(comment: Git.Comment, repositoryId: string, pullRequestId: number, threadId: number, commentId: number, project?: string): Promise<Git.Comment>;
    /**
     * Create a thread in a pull request.
     * 
     * @param commentThread - The thread to create. Thread must contain at least one comment.
     * @param repositoryId - Repository ID of the pull request's target branch.
     * @param pullRequestId - ID of the pull request.
     * @param project - Project ID or project name
     */
    createThread(commentThread: Git.GitPullRequestCommentThread, repositoryId: string, pullRequestId: number, project?: string): Promise<Git.GitPullRequestCommentThread>;
    /**
     * Update a thread in a pull request.
     * 
     * @param commentThread - The thread content that should be updated.
     * @param repositoryId - The repository ID of the pull request's target branch.
     * @param pullRequestId - ID of the pull request.
     * @param threadId - ID of the thread to update.
     * @param project - Project ID or project name
     */
    updateThread(commentThread: Git.GitPullRequestCommentThread, repositoryId: string, pullRequestId: number, threadId: number, project?: string): Promise<Git.GitPullRequestCommentThread>;
    /**
     * Creating, updating, or deleting refs(branches).
     * 
     * @param refUpdates - List of ref updates to attempt to perform
     * @param repositoryId - The name or ID of the repository.
     * @param project - Project ID or project name
     * @param projectId - ID or name of the team project. Optional if specifying an ID for repository.
     */
    updateRefs(refUpdates: Git.GitRefUpdate[], repositoryId: string, project?: string, projectId?: string): Promise<Git.GitRefUpdateResult[]>;
}

class GitRestClient extends RestClientBase implements IGitRestClient {
    constructor(options: IVssRestClientOptions) {
        super(options);
    }

    /**
     * Add a like on a comment.
     * 
     * @param repositoryId - The repository ID of the pull request's target branch.
     * @param pullRequestId - ID of the pull request.
     * @param threadId - The ID of the thread that contains the comment.
     * @param commentId - The ID of the comment.
     * @param project - Project ID or project name
     */
    public async createLike(
        repositoryId: string,
        pullRequestId: number,
        threadId: number,
        commentId: number,
        project?: string
        ): Promise<void> {

        return this.beginRequest<void>({
            apiVersion: "5.0-preview.1",
            method: "POST",
            routeTemplate: "{project}/_apis/git/repositories/{repositoryId}/pullRequests/{pullRequestId}/threads/{threadId}/comments/{commentId}/likes",
            routeValues: {
                project: project,
                repositoryId: repositoryId,
                pullRequestId: pullRequestId,
                threadId: threadId,
                commentId: commentId
            }
        });
    }

    /**
     * Delete a like on a comment.
     * 
     * @param repositoryId - The repository ID of the pull request's target branch.
     * @param pullRequestId - ID of the pull request.
     * @param threadId - The ID of the thread that contains the comment.
     * @param commentId - The ID of the comment.
     * @param project - Project ID or project name
     */
    public async deleteLike(
        repositoryId: string,
        pullRequestId: number,
        threadId: number,
        commentId: number,
        project?: string
        ): Promise<void> {

        return this.beginRequest<void>({
            apiVersion: "5.0-preview.1",
            method: "DELETE",
            routeTemplate: "{project}/_apis/git/repositories/{repositoryId}/pullRequests/{pullRequestId}/threads/{threadId}/comments/{commentId}/likes",
            routeValues: {
                project: project,
                repositoryId: repositoryId,
                pullRequestId: pullRequestId,
                threadId: threadId,
                commentId: commentId
            }
        });
    }

    /**
     * Retrieve all conflicts for a pull request
     * 
     * @param repositoryId - The repository of the Pull Request.
     * @param pullRequestId - The pull request ID.
     * @param project - Project ID or project name
     * @param skip - Conflicts to skip.
     * @param top - Conflicts to return after skip.
     * @param includeObsolete - Includes obsolete conflicts.
     * @param excludeResolved - Excludes conflicts already resolved.
     * @param onlyResolved - Returns only the conflicts that are resolved.
     */
    public async getPullRequestConflicts(
        repositoryId: string,
        pullRequestId: number,
        project?: string,
        skip?: number,
        top?: number,
        includeObsolete?: boolean,
        excludeResolved?: boolean,
        onlyResolved?: boolean
        ): Promise<Git.GitConflict[]> {

        const queryValues: any = {
            '$skip': skip,
            '$top': top,
            includeObsolete: includeObsolete,
            excludeResolved: excludeResolved,
            onlyResolved: onlyResolved
        };

        return this.beginRequest<Git.GitConflict[]>({
            apiVersion: "5.0-preview.1",
            routeTemplate: "{project}/_apis/git/repositories/{repositoryId}/pullRequests/{pullRequestId}/conflicts/{conflictId}",
            routeValues: {
                project: project,
                repositoryId: repositoryId,
                pullRequestId: pullRequestId
            },
            queryParams: queryValues
        });
    }

    /**
     * Create a tag (if that does not exists yet) and add that as a label (tag) for a specified pull request. The only required field is the name of the new label (tag).
     * 
     * @param label - Label to assign to the pull request.
     * @param repositoryId - The repository ID of the pull request’s target branch.
     * @param pullRequestId - ID of the pull request.
     * @param project - Project ID or project name
     * @param projectId - Project ID or project name.
     */
    public async createPullRequestLabel(
        label: TFS_Platform.WebApiCreateTagRequestData,
        repositoryId: string,
        pullRequestId: number,
        project?: string,
        projectId?: string
        ): Promise<TFS_Platform.WebApiTagDefinition> {

        const queryValues: any = {
            projectId: projectId
        };

        return this.beginRequest<TFS_Platform.WebApiTagDefinition>({
            apiVersion: "5.0-preview.1",
            method: "POST",
            routeTemplate: "{project}/_apis/git/repositories/{repositoryId}/pullRequests/{pullRequestId}/labels/{labelIdOrName}",
            routeValues: {
                project: project,
                repositoryId: repositoryId,
                pullRequestId: pullRequestId
            },
            queryParams: queryValues,
            body: label
        });
    }

    /**
     * Removes a label (tag) from the set of those assigned to the pull request. The tag itself will not be deleted.
     * 
     * @param repositoryId - The repository ID of the pull request’s target branch.
     * @param pullRequestId - ID of the pull request.
     * @param labelIdOrName - The name or ID of the label requested.
     * @param project - Project ID or project name
     * @param projectId - Project ID or project name.
     */
    public async deletePullRequestLabels(
        repositoryId: string,
        pullRequestId: number,
        labelIdOrName: string,
        project?: string,
        projectId?: string
        ): Promise<void> {

        const queryValues: any = {
            projectId: projectId
        };

        return this.beginRequest<void>({
            apiVersion: "5.0-preview.1",
            method: "DELETE",
            routeTemplate: "{project}/_apis/git/repositories/{repositoryId}/pullRequests/{pullRequestId}/labels/{labelIdOrName}",
            routeValues: {
                project: project,
                repositoryId: repositoryId,
                pullRequestId: pullRequestId,
                labelIdOrName: labelIdOrName
            },
            queryParams: queryValues
        });
    }

    /**
     * Add a reviewer to a pull request or cast a vote.
     * 
     * @param reviewer - Reviewer's vote.\<br /\>If the reviewer's ID is included here, it must match the reviewerID parameter.\<br /\>Reviewers can set their own vote with this method.  When adding other reviewers, vote must be set to zero.
     * @param repositoryId - The repository ID of the pull request's target branch.
     * @param pullRequestId - ID of the pull request.
     * @param reviewerId - ID of the reviewer.
     * @param project - Project ID or project name
     */
    public async createPullRequestReviewer(
        reviewer: Git.IdentityRefWithVote,
        repositoryId: string,
        pullRequestId: number,
        reviewerId: string,
        project?: string
        ): Promise<Git.IdentityRefWithVote> {

        return this.beginRequest<Git.IdentityRefWithVote>({
            apiVersion: "5.0-preview.1",
            method: "PUT",
            routeTemplate: "{project}/_apis/git/repositories/{repositoryId}/pullRequests/{pullRequestId}/reviewers/{reviewerId}",
            routeValues: {
                project: project,
                repositoryId: repositoryId,
                pullRequestId: pullRequestId,
                reviewerId: reviewerId
            },
            body: reviewer
        });
    }

    /**
     * Add an unmaterialized identity to the reviewers of a pull request.
     * 
     * @param reviewer - Reviewer to add to the pull request.
     * @param repositoryId - The repository ID of the pull request's target branch.
     * @param pullRequestId - ID of the pull request.
     * @param project - Project ID or project name
     */
    public async createUnmaterializedPullRequestReviewer(
        reviewer: Git.IdentityRefWithVote,
        repositoryId: string,
        pullRequestId: number,
        project?: string
        ): Promise<Git.IdentityRefWithVote> {

        return this.beginRequest<Git.IdentityRefWithVote>({
            apiVersion: "5.0-preview.1",
            method: "PUT",
            routeTemplate: "{project}/_apis/git/repositories/{repositoryId}/pullRequests/{pullRequestId}/reviewers/{reviewerId}",
            routeValues: {
                project: project,
                repositoryId: repositoryId,
                pullRequestId: pullRequestId
            },
            body: reviewer
        });
    }

    /**
     * Remove a reviewer from a pull request.
     * 
     * @param repositoryId - The repository ID of the pull request's target branch.
     * @param pullRequestId - ID of the pull request.
     * @param reviewerId - ID of the reviewer to remove.
     * @param project - Project ID or project name
     */
    public async deletePullRequestReviewer(
        repositoryId: string,
        pullRequestId: number,
        reviewerId: string,
        project?: string
        ): Promise<void> {

        return this.beginRequest<void>({
            apiVersion: "5.0-preview.1",
            method: "DELETE",
            routeTemplate: "{project}/_apis/git/repositories/{repositoryId}/pullRequests/{pullRequestId}/reviewers/{reviewerId}",
            routeValues: {
                project: project,
                repositoryId: repositoryId,
                pullRequestId: pullRequestId,
                reviewerId: reviewerId
            }
        });
    }

    /**
     * Edit a reviewer entry. These fields are patchable: isFlagged, hasDeclined
     * 
     * @param reviewer - Reviewer data.\<br /\>If the reviewer's ID is included here, it must match the reviewerID parameter.
     * @param repositoryId - The repository ID of the pull request's target branch.
     * @param pullRequestId - ID of the pull request.
     * @param reviewerId - ID of the reviewer.
     * @param project - Project ID or project name
     */
    public async updatePullRequestReviewer(
        reviewer: Git.IdentityRefWithVote,
        repositoryId: string,
        pullRequestId: number,
        reviewerId: string,
        project?: string
        ): Promise<Git.IdentityRefWithVote> {

        return this.beginRequest<Git.IdentityRefWithVote>({
            apiVersion: "5.0-preview.1",
            method: "PATCH",
            routeTemplate: "{project}/_apis/git/repositories/{repositoryId}/pullRequests/{pullRequestId}/reviewers/{reviewerId}",
            routeValues: {
                project: project,
                repositoryId: repositoryId,
                pullRequestId: pullRequestId,
                reviewerId: reviewerId
            },
            body: reviewer
        });
    }

    /**
     * Update a pull request
     * 
     * @param gitPullRequestToUpdate - The pull request content that should be updated.
     * @param repositoryId - The repository ID of the pull request's target branch.
     * @param pullRequestId - ID of the pull request to update.
     * @param project - Project ID or project name
     */
    public async updatePullRequest(
        gitPullRequestToUpdate: Git.GitPullRequest,
        repositoryId: string,
        pullRequestId: number,
        project?: string
        ): Promise<Git.GitPullRequest> {

        return this.beginRequest<Git.GitPullRequest>({
            apiVersion: "5.0-preview.1",
            method: "PATCH",
            routeTemplate: "{project}/_apis/git/repositories/{repositoryId}/pullRequests/{pullRequestId}",
            routeValues: {
                project: project,
                repositoryId: repositoryId,
                pullRequestId: pullRequestId
            },
            body: gitPullRequestToUpdate
        });
    }

    /**
     * Create a comment on a specific thread in a pull request (up to 500 comments can be created per thread).
     * 
     * @param comment - The comment to create. Comments can be up to 150,000 characters.
     * @param repositoryId - The repository ID of the pull request's target branch.
     * @param pullRequestId - ID of the pull request.
     * @param threadId - ID of the thread that the desired comment is in.
     * @param project - Project ID or project name
     */
    public async createComment(
        comment: Git.Comment,
        repositoryId: string,
        pullRequestId: number,
        threadId: number,
        project?: string
        ): Promise<Git.Comment> {

        return this.beginRequest<Git.Comment>({
            apiVersion: "5.0-preview.1",
            method: "POST",
            routeTemplate: "{project}/_apis/git/repositories/{repositoryId}/pullRequests/{pullRequestId}/threads/{threadId}/comments/{commentId}",
            routeValues: {
                project: project,
                repositoryId: repositoryId,
                pullRequestId: pullRequestId,
                threadId: threadId
            },
            body: comment
        });
    }

    /**
     * Delete a comment associated with a specific thread in a pull request.
     * 
     * @param repositoryId - The repository ID of the pull request's target branch.
     * @param pullRequestId - ID of the pull request.
     * @param threadId - ID of the thread that the desired comment is in.
     * @param commentId - ID of the comment.
     * @param project - Project ID or project name
     */
    public async deleteComment(
        repositoryId: string,
        pullRequestId: number,
        threadId: number,
        commentId: number,
        project?: string
        ): Promise<void> {

        return this.beginRequest<void>({
            apiVersion: "5.0-preview.1",
            method: "DELETE",
            routeTemplate: "{project}/_apis/git/repositories/{repositoryId}/pullRequests/{pullRequestId}/threads/{threadId}/comments/{commentId}",
            routeValues: {
                project: project,
                repositoryId: repositoryId,
                pullRequestId: pullRequestId,
                threadId: threadId,
                commentId: commentId
            }
        });
    }

    /**
     * Update a comment associated with a specific thread in a pull request.
     * 
     * @param comment - The comment content that should be updated. Comments can be up to 150,000 characters.
     * @param repositoryId - The repository ID of the pull request's target branch.
     * @param pullRequestId - ID of the pull request.
     * @param threadId - ID of the thread that the desired comment is in.
     * @param commentId - ID of the comment to update.
     * @param project - Project ID or project name
     */
    public async updateComment(
        comment: Git.Comment,
        repositoryId: string,
        pullRequestId: number,
        threadId: number,
        commentId: number,
        project?: string
        ): Promise<Git.Comment> {

        return this.beginRequest<Git.Comment>({
            apiVersion: "5.0-preview.1",
            method: "PATCH",
            routeTemplate: "{project}/_apis/git/repositories/{repositoryId}/pullRequests/{pullRequestId}/threads/{threadId}/comments/{commentId}",
            routeValues: {
                project: project,
                repositoryId: repositoryId,
                pullRequestId: pullRequestId,
                threadId: threadId,
                commentId: commentId
            },
            body: comment
        });
    }

    /**
     * Create a thread in a pull request.
     * 
     * @param commentThread - The thread to create. Thread must contain at least one comment.
     * @param repositoryId - Repository ID of the pull request's target branch.
     * @param pullRequestId - ID of the pull request.
     * @param project - Project ID or project name
     */
    public async createThread(
        commentThread: Git.GitPullRequestCommentThread,
        repositoryId: string,
        pullRequestId: number,
        project?: string
        ): Promise<Git.GitPullRequestCommentThread> {

        return this.beginRequest<Git.GitPullRequestCommentThread>({
            apiVersion: "5.0-preview.1",
            method: "POST",
            routeTemplate: "{project}/_apis/git/repositories/{repositoryId}/pullRequests/{pullRequestId}/threads/{threadId}",
            routeValues: {
                project: project,
                repositoryId: repositoryId,
                pullRequestId: pullRequestId
            },
            body: commentThread
        });
    }

    /**
     * Update a thread in a pull request.
     * 
     * @param commentThread - The thread content that should be updated.
     * @param repositoryId - The repository ID of the pull request's target branch.
     * @param pullRequestId - ID of the pull request.
     * @param threadId - ID of the thread to update.
     * @param project - Project ID or project name
     */
    public async updateThread(
        commentThread: Git.GitPullRequestCommentThread,
        repositoryId: string,
        pullRequestId: number,
        threadId: number,
        project?: string
        ): Promise<Git.GitPullRequestCommentThread> {

        return this.beginRequest<Git.GitPullRequestCommentThread>({
            apiVersion: "5.0-preview.1",
            method: "PATCH",
            routeTemplate: "{project}/_apis/git/repositories/{repositoryId}/pullRequests/{pullRequestId}/threads/{threadId}",
            routeValues: {
                project: project,
                repositoryId: repositoryId,
                pullRequestId: pullRequestId,
                threadId: threadId
            },
            body: commentThread
        });
    }

    /**
     * Creating, updating, or deleting refs(branches).
     * 
     * @param refUpdates - List of ref updates to attempt to perform
     * @param repositoryId - The name or ID of the repository.
     * @param project - Project ID or project name
     * @param projectId - ID or name of the team project. Optional if specifying an ID for repository.
     */
    public async updateRefs(
        refUpdates: Git.GitRefUpdate[],
        repositoryId: string,
        project?: string,
        projectId?: string
        ): Promise<Git.GitRefUpdateResult[]> {

        const queryValues: any = {
            projectId: projectId
        };

        return this.beginRequest<Git.GitRefUpdateResult[]>({
            apiVersion: "5.0-preview.1",
            method: "POST",
            routeTemplate: "{project}/_apis/git/repositories/{repositoryId}/refs/{*filter}",
            routeValues: {
                project: project,
                repositoryId: repositoryId
            },
            queryParams: queryValues,
            body: refUpdates
        });
    }

}

export const GitClientName = "Repos.PRDetails.Git.IGitRestClient";

export function getGitClient(pageContext: IVssPageContext, options?: IVssRestClientOptions): IGitRestClient {
    return pageContext.getRestClient<IGitRestClient>(GitClientName, options);
}

RestClients.add(GitClientName, { 
    factory: GitRestClient,
    options: {
        resourceAreaId: "4e080c62-fa21-4fbc-8fef-2a10a2b38049",
        serviceInstanceType: "00025394-6065-48ca-87d9-7f5672854ef7"
    }
});
